import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { useNavigate } from "react-router-dom";
import { AddRounded, DeleteRounded, DescriptionRounded, DownloadRounded, InfoRounded, LockOpenRounded, LockRounded, LoopRounded, WarningRounded } from "@mui/icons-material";
import { Button, Stack, Tooltip, useTheme } from "@mui/material";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { STATE_DISABLE_TAB, STATE_REPORT_ACTIVE_FILTERS, STATE_REPORT_TOTAL_COUNT, STATE_REPORT_UPDATING } from "../constants/MobilityStoreRecoil";
import { useMobilityStoreRepository } from "../../../api/repositories/MobilityStoreRepository";
import { formatDate } from "../../../utils/FormatUtils";
import { downloadJSONAsXLSX } from "../../../utils/ExcelUtility";
import { processReportJson } from "./ReportTableUtil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import FinanceDialog from "../../../component/FinanceDialog";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { MS_AUTOMATIC_BILLING, MS_CREATE_PRINTOUTS, MS_DELETE_REPORT, MS_DOWNLOAD_REPORT, MS_REPROCESS_REPORT, MS_VIEW_REPORT_DETAIL } from "../../../utils/FinanceScope";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

const ReportTable = () => {

    const UNIQUE_ID = "fnn-ms-report";

    const { t } = useTranslation('finance', { keyPrefix: 'mobility-store' });
    const { t: tClusters } = useTranslation('finance', { keyPrefix: 'clusters' });
    const { getReports, getPrintoutsByReportId, deletePrintouts, reprocessPrintouts, switchReportManualBilling } = useMobilityStoreRepository();
    const hasScope = useHasScope;
    const [updating, setUpdating] = useRecoilState(STATE_REPORT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const setTotalData = useSetRecoilState(STATE_REPORT_TOTAL_COUNT);
    const [openDeleteModal, setOpendeleteModal] = useState<boolean>(false);
    const [reportToDelete, setReportToDelete] = useState({ id: '', name: '' })
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [disableTab, setDisableTab] = useRecoilState(STATE_DISABLE_TAB);
    const resetDisableTab = useResetRecoilState(STATE_DISABLE_TAB);
    resetDisableTab();
    const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);

    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);
    const agGridLocale = useAgGridLocale();
    const navigate = useNavigate();

    const {  globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");


    const columnDefs : ColDef[] = [
        {
            field: 'name',
            headerName: t('tables.report.name'),
            headerTooltip: t('tables.report.name'),
            unSortIcon: true,
            lockVisible: true,

        },
        {
            field: 'created',
            headerName: t('tables.report.creation-date'),
            headerTooltip: t('tables.report.creation-date'),
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value),
        },
        {
            field: 'printoutCount',
            headerName: t('tables.report.printouts-count'),
            headerTooltip: t('tables.report.printouts-count'),
            unSortIcon: true
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let manualBilling = params.data.manualBillingCount === 0;
                return {
                    component: () =>
                        <Stack direction={"row"} justifyContent={"center"}>
                            {hasScope(MS_AUTOMATIC_BILLING) &&
                            <IconButton sx={{ color: theme.palette.accent.main }}
                                        onClick={() => {
                                            switchReportManualBilling(params.data.id, !manualBilling).then(() => {
                                                setSummaryUpdating(true);
                                                setUpdating(true);
                                            });
                                        }}>
                                <Tooltip title={t('tooltips.manual_billing.' + manualBilling)} arrow placement="left">
                                    {manualBilling ? <LockRounded/> : <LockOpenRounded/>}
                                </Tooltip>
                            </IconButton>
                            }
                            {(hasScope(MS_VIEW_REPORT_DETAIL) || hasScope(MS_DOWNLOAD_REPORT) || hasScope(MS_REPROCESS_REPORT) || hasScope(MS_DELETE_REPORT)) &&
                            <FinanceDynamicActions>
                                {hasScope(MS_VIEW_REPORT_DETAIL) &&
                                <IconButton sx={{ color: theme.palette.accent.main }} onClick={() => {
                                    setDisableTab(true);
                                    window.open('/finance/mobility-store/report/' + params.data.id, "_blank");
                                }}>
                                    <DescriptionRounded/>
                                </IconButton>
                                }
                                {hasScope(MS_DOWNLOAD_REPORT) &&
                                <IconButton sx={{ color: theme.palette.accent.main }} onClick={() => {
                                    getPrintoutsByReportId(params.data.id).then(it => {
                                        downloadJSONAsXLSX(
                                            params.data.name,
                                            processReportJson(it.result.content, tClusters, t)
                                        );
                                    })
                                }}>
                                    <DownloadRounded/>
                                </IconButton>
                                }
                                {hasScope(MS_REPROCESS_REPORT) &&
                                <IconButton sx={{ color: theme.palette.accent.main }} onClick={() => {
                                    reprocessPrintouts(params.data.id).then(() => {
                                        setUpdating(true);
                                        setSummaryUpdating(true);
                                    })
                                }}>
                                    <LoopRounded/>
                                </IconButton>
                                }
                                {hasScope(MS_DELETE_REPORT) &&
                                <IconButton sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                setOpendeleteModal(true);
                                                setReportToDelete({ id: params.data.id, name: params.data.name });
                                            }}>
                                    <DeleteRounded/>
                                </IconButton>
                                }
                            </FinanceDynamicActions>
                             }

                        </Stack>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.report.overlays.no-rows.title'),
            content: t('tables.report.overlays.no-rows.content')
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
            <></>
        ),
        renderActions: () => (
            <Stack>
                {hasScope(MS_CREATE_PRINTOUTS) &&
                <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                    navigate("/finance/mobility-store/printouts-wizard")
                }} startIcon={<AddRounded/>}>{t('buttons.add-printout')}</Button>
            }
            </Stack>
        )
    }

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;
                        setTotalData(result.page.totalElements)
                        if (result.page.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.page.totalElements
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                noRowsOverlayComponent={noRowsOverlayComponent}
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={'multiple'}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
            >
            </AgGridCustomTable>
            <FinanceDialog
                title={t('tables.report.delete-modal.title')}
                content={t('tables.report.delete-modal.content', { reportName: reportToDelete.name })}
                cancelButton={t('tables.report.delete-modal.cancel')}
                callback={() => {
                    deletePrintouts(reportToDelete.id).then((res) => {
                        if (res.status === 'OK') {
                            setUpdating(true);
                            setSummaryUpdating(true);
                        } else {
                            setOpenErrorDialog(true)
                            setErrorMessage(res.message)
                        }
                    })
                }}
                confirmButton={t('tables.report.delete-modal.confirm')}
                open={openDeleteModal}
                onClose={() => {
                    setOpendeleteModal(false)
                }}
                titleIcon={<InfoRounded sx={{ color: 'accent.main' }}/>}
            />
            <FinanceDialog
                title={t('tables.report.error-dialog.title')}
                content={t('tables.report.error-dialog.errors.' + errorMessage)}
                cancelButton={t('tables.report.error-dialog.cancel')}
                open={openErrorDialog}
                onClose={() => {
                    setOpenErrorDialog(false)
                }}
                titleIcon={<WarningRounded sx={{ color: 'error.main' }}/>}
            />
        </>
    );
}

export default ReportTable;

